import {
  createFrontendModule,
  PageBlueprint,
  PluginHeaderActionBlueprint,
} from '@backstage/frontend-plugin-api';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import catalogUnprocessedEntitiesPlugin from '@backstage/plugin-catalog-unprocessed-entities/alpha';
import mcpChatPlugin from '@internal/backstage-plugin-mcp-chat/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import { RequireFeatureFlag } from '../components/RequireFeatureFlag';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  CatalogImportPage,
  CatalogImportSupportButton,
} from '@internal/plugin-catalog';
import { ErrorPage, XelerateLogoIconMono } from '@xlrt/components';
import {
  RiCookieLine,
  RiFileAddLine,
  RiRobot2Line,
  RiShieldUserLine,
} from '@remixicon/react';
import SettingsPlugin from '@backstage/plugin-user-settings/alpha';
import devtoolsPlugin from '@backstage/plugin-devtools/alpha';
import { mcpChatAccessPermission } from '@internal/plugin-permissions-common';

// ==========================================
// 1. BLUEPRINT DEFINITIONS
// ==========================================

const privacyPolicyPage = PageBlueprint.make({
  name: 'privacy-policy-page',
  params: {
    path: '/privacy-policy',
    title: 'Privacy Policy',
    icon: <RiShieldUserLine />,
    loader: () =>
      import('../components/policy-pages/PrivacyPolicyPage').then(m => (
        <m.PrivacyPolicyPage />
      )),
  },
});

const cookiePolicyPage = PageBlueprint.make({
  name: 'cookie-policy-page',
  params: {
    path: '/cookie-policy',
    title: 'Cookie Policy',
    icon: <RiCookieLine />,
    loader: () =>
      import('../components/policy-pages/CookiePolicyPage').then(m => (
        <m.CookiePolicyPage />
      )),
  },
});

const aboutPage = PageBlueprint.make({
  name: 'about-page',
  params: {
    path: '/about',
    title: 'About',
    icon: <XelerateLogoIconMono />,
    loader: () =>
      import('../components/about/AboutPage').then(m => <m.AboutPage />),
  },
});

// Replaces the core import page and keeps its path and route ref.
const catalogImportPage = catalogImportPlugin
  .getExtension('page:catalog-import')
  .override({
    params: {
      title: 'Register an existing CARIAD component',
      icon: <RiFileAddLine />,
      loader: async () => (
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      ),
    },
  });

// The not-found page is full-bleed and keeps no page header.
const notFoundPage = PageBlueprint.make({
  name: 'not-found-page',
  params: {
    path: '*',
    noHeader: true,
    loader: async () => (
      <ErrorPage status="404" statusMessage="PAGE NOT FOUND" />
    ),
  },
});

// ==========================================
// 2. MODULAR MODULE EXPORTS
// ==========================================

export const routesAlphaPages = createFrontendModule({
  pluginId: 'app',
  extensions: [privacyPolicyPage, cookiePolicyPage, aboutPage, notFoundPage],
});

const catalogImportSupportButton = PluginHeaderActionBlueprint.make({
  name: 'support',
  params: {
    loader: async () => <CatalogImportSupportButton />,
  },
});

export const catalogImportOverridesModule = createFrontendModule({
  pluginId: 'catalog-import',
  extensions: [catalogImportPage, catalogImportSupportButton],
});

export const mcpChatPage = createFrontendModule({
  pluginId: 'mcp-chat',
  extensions: [
    mcpChatPlugin.getExtension('page:mcp-chat').override({
      params: {
        title: 'Xpert',
        // The plugin icon is white, made for the purple Ask xpert button.
        icon: <RiRobot2Line />,
        loader: async () => {
          const { NewChatPage } =
            await import('@internal/backstage-plugin-mcp-chat');
          return (
            <RequireFeatureFlag flag="enable-ai-chat">
              {/* Members of aiusers and admins, as the custom permission policy decides */}
              <RequirePermission permission={mcpChatAccessPermission}>
                <NewChatPage />
              </RequirePermission>
            </RequireFeatureFlag>
          );
        },
      },
    }),
  ],
});

export {
  catalogGraphPlugin,
  catalogUnprocessedEntitiesPlugin,
  mcpChatPlugin,
  notificationsPlugin,
  devtoolsPlugin,
  SettingsPlugin,
};
